<template>
    <div v-if="field.type === 'tablelist' && shouldShowField"
         class="col-12"
         :data-field-code="field.code">
        <table-list
            :parent-field-data="field"
            :old-data="oldDataStringForTable"
            :table="tableData"
            :is-editible="isEditable"
            @updateRow="updateTableRow"
            @input="onTableListInput"
            :validation-errors="validationErrors"
        />
    </div>

    <h6 class="col-12 d-flex align-items-center"
        v-else-if="field.type === 'title'"
        :data-field-code="field.code">
        {{ field['title_' + lang] }}

        <info-label
            v-if="field.settings && field.settings.tooltip"
            :text="field.settings.tooltip"
        />
    </h6>

    <div v-else-if="shouldShowField"
         :data-field-code="field.code"
         :class="tableView ? 'w-100'
                        : (['informationblock','textarea'].includes(field.type) ? 'col-12'
                                                                                : 'col-xl-6 col-12')">
        <div :class="tableView ? 'w-100' : 'form-group margin-bottom-12 h-100'">

            <checkbox
                v-if="field.type === 'checkbox'"
                :old-data="oldData"
                v-model="currentFieldValue"
                :field="field"
                :is-editible="isEditable"
                :table-view="tableView"
                :validation-errors="getFieldError"
            />

            <dictionary
                v-else-if="field.type === 'dictionary'"
                :old-data="oldData"
                v-model="currentFieldValue"
                :field="field"
                :table-view="tableView"
                :is-editible="isEditable"
                :validation-errors="getFieldError"
            />

            <text-area
                v-else-if="field.type === 'textarea'"
                :old-data="oldData"
                v-model="currentFieldValue"
                :field="field"
                :table-view="tableView"
                :is-editible="isEditable"
                :validation-errors="getFieldError"
            />

            <text-field
                v-else-if="!['tablelist', 'checkbox', 'document', 'informationblock', 'dictionary', 'textarea'].includes(field.type)"
                :old-data="oldData"
                v-model="currentFieldValue"
                :field="field"
                :table-view="tableView"
                :is-editible="isEditable"
                :validation-errors="getFieldError"
            />

            <div
                v-if="field.type === 'informationblock' && field.settings && field.settings['value_' + lang]"
                v-html="field.settings['value_' + lang]"
            />
            <p v-if="field['note_' + lang]" class="note">{{ field['note_' + lang] }}</p>
        </div>
    </div>
</template>

<script>
import Checkbox from './Checkbox.vue';
import Dictionary from './Dictionary.vue';
import TextArea from './TextArea.vue';
import TextField from './TextField.vue';
import TableList from './Tablelist.vue';
import InfoLabel from '../ui/InfoLabel.vue';
import {setFormValue, getFormValue} from './formStore';

export default {
    name: 'fields',
    components: {Checkbox, Dictionary, TextArea, TextField, TableList, InfoLabel},

    props: {
        field: {type: Object, required: true},
        oldData: {type: [String, Number, Array, Object, Boolean, null], default: ''},
        isEditable: {type: Boolean, default: true},
        tableView: {type: Boolean, default: false},
        validationErrors: {type: Object, default: () => ({})},
        user: {type: Object, default: () => ({})},
    },

    emits: ['updateField', 'valueChanged', 'updateRow'],

    data() {
        return {
            lang: 'ru',
            tableData: '[]',
            currentFieldValue: null,
        };
    },

    computed: {
        getFieldError() {
            return this.field && this.validationErrors ? this.validationErrors[this.field.code] : [];
        },

        // Условие показа поля: смотрим на settings.conditional_field и проверяем «заполненность» зависимого значения
        shouldShowField() {
            const key = this.field?.settings?.conditional_field;
            if (!key) return true;
            const v = getFormValue(key);
            return this.isFilledForCondition(v);
        },

        oldDataStringForTable() {
            const v = this.oldData;
            if (typeof v === 'string') return v;
            try { return JSON.stringify(v ?? []); } catch { return '[]'; }
        },
    },

    created() {
        this.tableData = this.stringifyColumns(this.field?.table_form);

        this._unwatchTableForm = this.$watch(
            () => this.field?.table_form,
            (v) => { this.tableData = this.stringifyColumns(v); },
            {immediate: false}
        );
    },

    mounted() {
        this.lang = document.querySelector('html')?.getAttribute('lang') || 'ru';

        if (this.field.type === 'title') {
            let settings = this.field.settings;
            if (Array.isArray(settings)) settings = settings.length > 0 ? settings[0] : {};
            // eslint-disable-next-line vue/no-mutating-props
            this.field.settings = settings || {};
        }

        this.currentFieldValue = this.normalizeByType(this.oldData);
        setFormValue(this.field.code, this.currentFieldValue);

        this.applyAutofill();
    },

    beforeUnmount() {
        if (this._unwatchTableForm) this._unwatchTableForm();
    },

    watch: {
        currentFieldValue(val) {
            const value = this.normalizeByType(val);
            this.$emit('updateField', value);
            this.$emit('valueChanged', {code: this.field.code, value});
            setFormValue(this.field.code, value);
        },

        oldData(newVal) {
            this.currentFieldValue = this.normalizeByType(newVal);
            setFormValue(this.field.code, this.currentFieldValue);
        },

        shouldShowField(visible) {
            if (!visible) {
                this.currentFieldValue = this.emptyByType(this.field.type);
                setFormValue(this.field.code, this.currentFieldValue);
            }
        },
    },

    methods: {
        onTableListInput(json) {
            const v = this.parseMaybeJson(json);
            this.currentFieldValue = Array.isArray(v) ? v : [];
        },

        stringifyColumns(v) {
            if (typeof v === 'string') return v;
            try { return JSON.stringify(v ?? []); } catch { return '[]'; }
        },

        parseMaybeJson(v) {
            if (typeof v !== 'string') return v;
            const s = v.trim();
            if (s === '' || s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined') return null;
            try { return JSON.parse(s); } catch { return v; }
        },

        emptyByType(t) {
            switch (t) {
            case 'checkbox': return false;
            case 'document':
            case 'tablelist': return [];
            case 'number': return null;
            case 'date': return null;
            case 'textarea':
            case 'text':
            case 'title':
            case 'informationblock':
            case 'phone':
            case 'email':
            case 'iinbin':
            case 'dictionary':
                return '';
            default: return null;
            }
        },

        // Нормализация входного значения под тип поля
        normalizeByType(value) {
            const t = this.field?.type;

            switch (t) {
            case 'checkbox':
                return (value === true || value === 1 || value === '1' || value === 'on' || value === 'true');

            case 'number': {
                if (typeof value === 'number') return value;
                if (typeof value === 'string') {
                    const s = value.trim();
                    if (s === '') return null;
                    const n = Number(s);
                    return Number.isFinite(n) ? n : null;
                }
                return value ?? null;
            }

            case 'date':
                if (typeof value === 'string') return value.trim() === '' ? null : value.trim();
                return value ?? null;

            case 'document': {
                const v = this.parseMaybeJson(value);
                return Array.isArray(v) ? v : [];
            }

            case 'dictionary': {
                if (value == null) return '';
                if (Array.isArray(value)) return value;
                if (typeof value === 'string') return value;
                if (typeof value === 'number') return String(value);
                try { return JSON.stringify(value); } catch { return ''; }
            }

            case 'tablelist': {
                const v = this.parseMaybeJson(value);
                return Array.isArray(v) ? v : [];
            }

            case 'textarea':
            case 'text':
            case 'title':
            case 'informationblock':
                return (value ?? '').toString();

            case 'phone':
            case 'iinbin':
                return value == null ? '' : value.toString().trim();

            case 'email':
                return value == null ? '' : value.toString().trim().toLowerCase();

            default:
                return value;
            }
        },

        isFilledForCondition(v) {
            if (v === true || v === 1 || v === '1') return true;

            if (typeof v === 'string') return v.trim().length > 0;

            if (Array.isArray(v)) {
                if (v.length === 0) return false;
                const looksLikeRows = v[0] && typeof v[0] === 'object' && !Array.isArray(v[0]);
                if (looksLikeRows) {
                    const rowHasAnyValue = (row) =>
                        Object.values(row || {}).some((cell) => {
                            if (cell === true || cell === 1 || cell === '1') return true;
                            if (typeof cell === 'number') return true;
                            if (typeof cell === 'string') return cell.trim().length > 0;
                            if (Array.isArray(cell)) return cell.length > 0;
                            if (cell && typeof cell === 'object') return Object.keys(cell).length > 0;
                            return false;
                        });
                    return v.some(rowHasAnyValue);
                }
                return v.length > 0;
            }

            if (v && typeof v === 'object') return true;

            if (typeof v === 'number') return true;

            return !!v;
        },

        updateTableRow(payload) {
            this.$emit('updateRow', payload);
        },

        applyAutofill() {
            const autofillKey = this.field?.autofill;
            if (!autofillKey) return;

            const source = this.user || {};
            let value = null;

            if (this.currentFieldValue !== null && this.currentFieldValue !== '' && this.currentFieldValue !== undefined) {
                return;
            }
            // Поиск нужных ключей в user
            if (source && Object.prototype.hasOwnProperty.call(source, autofillKey)) {
                value = source[autofillKey];
            }

            // Поиск в user.company
            if (!value && source.company && Object.prototype.hasOwnProperty.call(source.company, autofillKey)) {
                value = source.company[autofillKey];
            }

            // Специальный кейс full_name
            if (!value && autofillKey === 'full_name' && source.full_name) {
                value = source.full_name;
            }

            if (value != null) {
                const normalized = this.normalizeByType(value);

                // Записываем в реактивное поле и обновляем поле
                this.currentFieldValue = normalized;
                setFormValue(this.field.code, normalized);
            }
        },
    },
};
</script>
